import { chooseDBTranslation } from 'utils/translations'
import {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  TableLayoutType,
  BorderStyle,
  AlignmentType,
  WidthType,
} from 'docx'
import { CVData } from 'types/printInterfaces'
import { IPersonSkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { roundToDecimal, skillLevels } from 'utils/utils'
import { contentWidth, fontSize, tableGapWidth } from './pageLayout'
import colors from 'constants/colors'
import { i18n as i18nType, TFunction } from 'i18next'

/** Width of each of the four skill table columns: name and details, twice. */
const skillColumnWidth = (contentWidth - tableGapWidth) / 4

/** Skill level legend, from weakest to strongest like in the PDF. */
const skillLegend: Array<[string, string]> = [
  ['1', 'print.skills.fundamentals'],
  ['2', 'print.skills.novice'],
  ['3', 'print.skills.intermediate'],
  ['4', 'print.skills.advanced'],
  ['5', 'print.skills.expert'],
]

/**
 * Generates a table of skills for a CV.
 *
 * @param {i18n} i18n - the internationalization object
 * @param {CVData} data - the data for the CV
 * @param {type} type - the type of skills to generate (e.g., 'skills', 'industry', 'role')
 * @param {t} t - the translation function
 * @param {boolean} hideLastUsed - whether to hide the last used date
 * @param {boolean} hideSkillExperience - whether to hide the skill experience
 * @return {Table} the generated table of skills
 * @notExported
 */
const skill = (
  i18n: i18nType,
  data: CVData,
  type: 'skills' | 'industries' | 'roles',
  t: TFunction,
  hideLastUsed: boolean,
  hideSkillExperience: boolean
): Table => {
  const items: IPersonSkillOrIndustryOrRole[] = data[type]

  const borders = {
    top: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    bottom: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    left: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    right: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
  }
  const spacing = {
    after: 160,
  }

  // level, experience and last used, like in the PDF
  const detailRun = { size: fontSize.small, color: colors.primary, font: { name: 'Raleway' } }

  interface Item {
    name: Paragraph
    level: Paragraph
  }

  const itemCells: Item[] = []
  for (const item of items) {
    const levelLabel = item.level ? skillLevels()[item.level - 1]?.label : undefined
    const level = levelLabel ? t(levelLabel) : ''
    itemCells.push({
      name: new Paragraph({
        keepLines: true,
        style: 'CaleoNormal',
        children: [
          new TextRun({
            text: item ? chooseDBTranslation(i18n, item.Skill).name : '',
            size: fontSize.small,
            font: {
              name: 'Raleway',
            },
          }),
        ],
        spacing,
      }),
      level: new Paragraph({
        keepLines: true,
        style: 'CaleoNormal',
        children: [
          new TextRun({
            text: level ? `${item.level}. ${level}` : '',
            ...detailRun,
          }),
          ...(!hideSkillExperience
            ? [
                new TextRun({
                  text: `${t('profile.skill.experience')}: ${
                    item.experienceMonths
                      ? `${roundToDecimal(item.experienceMonths / 12, 1)} ${t('profile.skill.years.abbr')}`
                      : '-'
                  }`,
                  ...detailRun,
                  break: 1,
                }),
              ]
            : []),
          ...(!hideLastUsed
            ? [
                new TextRun({
                  text: `${t('profile.skill.lastUsed')}: ${item.lastUsed ? item.lastUsed : ''}`,
                  ...detailRun,
                  break: 1,
                }),
              ]
            : []),
        ],
        alignment: AlignmentType.LEFT,
        spacing,
      }),
    })
  }

  const firstColCells = itemCells
    .map((cell, i) => {
      if (i + 1 <= (itemCells.length + 1) / 2) return cell
    })
    .filter(cell => cell !== undefined) as Item[]

  const secondColCells = itemCells
    .map((cell, i) => {
      if (i + 1 > (itemCells.length + 1) / 2) return cell
    })
    .filter(cell => cell !== undefined) as Item[]

  const itemRows: TableRow[] = firstColCells.map((cell, i) => {
    return new TableRow({
      cantSplit: true,
      children: [
        new TableCell({
          children: [cell.name],
          width: {
            size: skillColumnWidth,
            type: WidthType.DXA,
          },
          borders,
        }),
        new TableCell({
          children: [cell.level],
          width: {
            size: skillColumnWidth,
            type: WidthType.DXA,
          },
          borders,
        }),
        new TableCell({
          children: [],
          width: {
            size: tableGapWidth,
            type: WidthType.DXA,
          },
          borders,
        }),
        new TableCell({
          children: secondColCells[i] ? [secondColCells[i].name] : [],
          width: {
            size: skillColumnWidth,
            type: WidthType.DXA,
          },
          borders,
        }),
        new TableCell({
          children: secondColCells[i] ? [secondColCells[i].level] : [],
          width: {
            size: skillColumnWidth,
            type: WidthType.DXA,
          },
          borders,
        }),
      ],
    })
  })

  itemRows.push(
    new TableRow({
      cantSplit: true,
      children: [
        new TableCell({
          children: [
            new Paragraph({
              keepLines: true,
              children: skillLegend.flatMap(([code, key], index) => [
                ...(index > 0 ? [new TextRun({ text: '|' })] : []),
                new TextRun({ text: `  ${code} - ${t(key)}  ` }),
              ]),
              style: 'GrayText',
            }),
          ],
          borders,
          columnSpan: 5,
        }),
      ],
    })
  )

  return new Table({
    rows: itemRows && itemRows.length ? itemRows : [],
    // without explicit widths docx defaults to a 100 twip wide table
    width: { size: contentWidth, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: [skillColumnWidth, skillColumnWidth, tableGapWidth, skillColumnWidth, skillColumnWidth],
  })
}

export default skill
